import React, { useEffect } from 'react'
import {
  ConfirmModal,
  ModalBig,
  ModalSmall,
  AlertModal,
  ModalFullscreen,
} from '@/modules/base/components/modal/index'
// eslint-disable-next-line camelcase
import { UNSAFE_renderElement, useGlobalPortal } from '@/hooks/useGlobalPortal'

const Components = {
  ConfirmModal,
  ModalBig,
  ModalSmall,
  AlertModal,
  ModalFullscreen,
}

/**
 * A number, or a string containing a number.
 */
export type Components = 'ModalSmall' | 'ModalBig' | 'ConfirmModal' | 'AlertModal' | 'ModalFullscreen';

/**
 * Props for the modal component.
 */
export type ModalProps = {
  component?: Components; // Modal component, defaults to 'ModalSmall'
  customComponent?: React.ElementType; // Custom component to be used instead of the default ones
  children?: React.ReactNode; // Children to put inside the component
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  props: { [key: string]: any } // Props to be passed to the component
}
 & (
    | { component?: Components; }
    | { component?: 'ConfirmModal';
        props: {
          title?: string;
          description?: string | object;
          debounceTimeConfirm?: number;
          abortButtonLabel?: string;
          confirmButtonLabel?: string;
          confirmButtonIcon?: React.ReactNode;
          onAbort?: () => void;
          onConfirm?: () => void;
          onPanic?: () => void;
          descriptionAsHtml?: boolean;
          paperClassName?: string;
        }
      }
    | { component?: 'AlertModal';
        props: {
          title?: string;
          children?: string | React.ReactNode;
          onConfirmClick?: () => void;
          classes?: Record<string, string>;
          buttonLabel?: string;
        }
      }
  )

type CloseFunction = () => void

/**
 * Function to create a modal.
 */
export type OpenModalCreate = (close: CloseFunction) => ModalProps;

const openModal = (renderElement) => (create:OpenModalCreate) : CloseFunction => {
  let closeFunction = null
  const onClose:CloseFunction = () => {
    closeFunction?.()
  }

  renderElement((closeFn) => {
    closeFunction = closeFn
    const modalProps = create(closeFn)
    const { component = 'ModalSmall', children, props, customComponent } = modalProps

    const Component = customComponent ?? Components[component]

    return (
      <Component open={true} {...props}>
        {children}
      </Component>
    )
  })

  return onClose
}

export function useOpenModal() {
  const renderElement = useGlobalPortal()
  if (!renderElement) {
    throw new Error('useOpenModal must be used within a GlobalPortalProvider')
  }

  return openModal(renderElement)
}

// eslint-disable-next-line camelcase
export function UNSAFE_openModal(create:OpenModalCreate) {
  // eslint-disable-next-line camelcase
  if (!UNSAFE_renderElement) {
    throw new Error('UNSAFE_renderElement must be used within a GlobalPortalProvider')
  }

  return openModal(UNSAFE_renderElement)(create)
}

export const modalConfirmation = (onConfirm, props) => {
  const { onAbort, children, ...rest } = props
  UNSAFE_openModal((close) => ({
    component: 'ConfirmModal',
    children,
    props: {
      onConfirm: () => {
        onConfirm()
        close()
      },
      onAbort: () => {
        onAbort?.()
        close()
      },
      ...rest,
    },
  }))
}
